import React from "react";
import { CheckCircle2, ShieldAlert } from "lucide-react";

/**
 * PoliciesChecklist — 22 individual rental-policy checkboxes.
 *
 * Every checkbox must be actively ticked before the customer can proceed
 * to signature. `onChange` receives the whole policies_accepted map so the
 * parent form can gate its "Next" button and forward the object to the
 * booking payload (which is persisted for audit).
 *
 * Order matches the legal ordering the owner supplied so the printed PDF
 * later can iterate the same keys.
 */
export const RENTAL_POLICIES = [
  { key: "no_smoking",            label: "No Smoking",                            note: "Detected smoke = full deposit forfeiture" },
  { key: "no_pets",               label: "No Pets",                               note: "Pet hair / odor = cleaning + damage fees" },
  { key: "no_racing",             label: "No Racing",                             note: "Any competitive driving voids the agreement" },
  { key: "no_burnouts",           label: "No Burnouts",                           note: "Tire damage = full retail replacement + labor" },
  { key: "no_drifting",           label: "No Drifting",                           note: "Detected via telematics — instant termination" },
  { key: "no_track_use",          label: "No Track / Circuit Use",                note: "Sanctioned or unsanctioned tracks alike" },
  { key: "no_off_roading",        label: "No Off-Roading",                        note: "Paved & maintained roads only" },
  { key: "no_towing",             label: "No Towing / Hauling",                   note: "Trailers, boats, other vehicles prohibited" },
  { key: "no_unauthorized_drivers", label: "No Unauthorized Drivers",             note: "Only named authorized drivers may operate the vehicle" },
  { key: "mileage_limits",        label: "Mileage Limits",                        note: "Overage billed at $0.45/mile" },
  { key: "fuel_policy",           label: "Fuel Policy",                           note: "Full tank in, full tank out — shortage billed at $9.99/gal" },
  { key: "cleaning_fees",         label: "Cleaning Fees",                         note: "Excessive dirt / stains / trash billed to card on file" },
  { key: "late_fees",             label: "Late Return Fees",                      note: "$50 for the first hour, hourly thereafter" },
  { key: "damage_responsibility", label: "Damage Responsibility",                 note: "Renter is responsible for all damage during the rental period" },
  { key: "parking_tickets",       label: "Parking Tickets",                       note: "All parking violations billed with a $25 admin fee" },
  { key: "toll_charges",          label: "Toll Charges",                          note: "All tolls billed with a $10 admin fee per unpaid toll" },
  { key: "speeding_tickets",      label: "Speeding Tickets",                      note: "Moving violations billed with a $50 admin fee" },
  { key: "vehicle_tracking",      label: "Vehicle Tracking Consent",              note: "GPS location is logged for the entire rental" },
  { key: "gps_monitoring",        label: "GPS Monitoring Consent",                note: "Speed, harsh braking, and location alerts are recorded" },
  { key: "remote_disablement",    label: "Remote Disablement Acknowledgment",     note: "Vehicle may be remotely immobilized for policy violations" },
  { key: "repossession",          label: "Repossession Clause",                   note: "Overdue vehicles may be repossessed at renter's expense" },
  { key: "deposit_authorization", label: "Security Deposit Authorization",        note: "Card on file may be charged for damages, fees, and unpaid balances" },
];

export default function PoliciesChecklist({ value, onChange, testId = "rental-policies-checklist" }) {
  const map = value || {};
  const total = RENTAL_POLICIES.length;
  const doneCount = RENTAL_POLICIES.filter((p) => !!map[p.key]).length;
  const allChecked = doneCount === total;
  const setOne = (key, checked) => {
    onChange({ ...map, [key]: checked });
  };
  const acceptAll = () => {
    const next = {};
    RENTAL_POLICIES.forEach((p) => { next[p.key] = true; });
    onChange(next);
  };
  return (
    <div data-testid={testId} className="rounded-2xl border border-cyan/25 bg-cyan/[0.03] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="label-caps text-cyan text-[10px]">Rules &amp; Policies · Each Must Be Acknowledged</div>
          <div className="text-xs text-white/60 mt-0.5">
            {allChecked
              ? "All 22 policies acknowledged."
              : `${doneCount} of ${total} acknowledged — every one is required to continue.`}
          </div>
        </div>
        <button
          type="button"
          data-testid={`${testId}-accept-all`}
          onClick={acceptAll}
          className={`px-3 py-1.5 rounded-full text-[10px] label-caps border transition ${
            allChecked
              ? "border-white/10 text-white/40 cursor-default"
              : "border-cyan/40 text-cyan hover:bg-cyan/5"
          }`}
          disabled={allChecked}
        >
          I acknowledge all
        </button>
      </div>

      {!allChecked && (
        <div className="rounded-lg bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-[11px] text-amber-200 flex items-start gap-2">
          <ShieldAlert size={12} className="mt-0.5 flex-shrink-0" />
          <span>These policies are legally binding. Read each one before ticking — they govern damage liability, tracking consent, and deposit forfeiture.</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {RENTAL_POLICIES.map((p) => {
          const checked = !!map[p.key];
          return (
            <label
              key={p.key}
              data-testid={`policy-${p.key}`}
              className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition ${
                checked
                  ? "border-cyan/40 bg-cyan/[0.06]"
                  : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
            >
              <input
                type="checkbox"
                data-testid={`policy-${p.key}-input`}
                checked={checked}
                onChange={(e) => setOne(p.key, e.target.checked)}
                className="mt-0.5 accent-cyan w-4 h-4 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white flex items-center gap-1.5">
                  <span className="font-medium">{p.label}</span>
                  {checked && <CheckCircle2 size={11} className="text-cyan" />}
                </div>
                <div className="text-[10px] text-white/50 leading-snug mt-0.5">{p.note}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
